import { API_BASE_URL } from '../config/apiConfig';
import {
  buildSessionHeaders,
  getClientAccessToken,
} from '../utils/sessionAuth';
import { parseAccountingImportFileClientSide } from '../utils/accountingImportFileFallback';
import {
  API_CACHE_TTL,
  buildApiCacheKey,
  cachedApiGet,
  clearApiRequestCache,
} from './apiRequestCache';
import {
  invalidateCarbonReadCache,
  invalidateComplianceReadCache,
  invalidateMsmeReadCache,
} from './workspaceCache';

type QueryParamValue = string | number | boolean | undefined | null;

function isLikelyTimeoutFailure(err: unknown): boolean {
  if (err instanceof Error && err.name === 'TimeoutError') {
    return true;
  }
  if (err instanceof DOMException && err.name === 'AbortError') {
    return /timeout|timed out|aborted due to timeout/i.test(err.message);
  }
  if (err instanceof Error) {
    return /timeout|timed out|aborted due to timeout/i.test(err.message);
  }
  return false;
}

function reportingTimeoutMessage(endpoint: string): string {
  if (endpoint.startsWith('/reporting/')) {
    return 'Report generation timed out. Please retry in a moment or use a shorter reporting period.';
  }
  if (endpoint.includes('import-accounting')) {
    return 'Accounting file import timed out. Try a smaller export or retry in a moment.';
  }
  return 'The request timed out. Please try again.';
}

function isLikelyNetworkFailure(err: unknown): boolean {
  if (err instanceof TypeError) {
    return true;
  }
  if (err instanceof Error) {
    return /failed to fetch|networkerror|load failed|network request failed/i.test(err.message);
  }
  return false;
}

function looksLikePdfBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 5
    && bytes[0] === 0x25 // %
    && bytes[1] === 0x50 // P
    && bytes[2] === 0x44 // D
    && bytes[3] === 0x46 // F
    && bytes[4] === 0x2d // -
  );
}

/**
 * Ensure report downloads are real PDF bytes with application/pdf type.
 * Prevents browser PDF viewers from showing "Failed to load PDF document."
 * when the API accidentally returns JSON/text with a PDF content-type.
 */
async function ensurePdfBlob(response: Response): Promise<Blob> {
  const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  if (looksLikePdfBytes(bytes)) {
    return new Blob([arrayBuffer], { type: 'application/pdf' });
  }

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    let text = '';
    try {
      text = typeof TextDecoder !== 'undefined'
        ? new TextDecoder().decode(bytes)
        : String.fromCharCode(...Array.from(bytes));
    } catch {
      text = '';
    }
    let message = 'Downloaded report is not a valid PDF';
    try {
      const json = JSON.parse(text);
      message = json?.message || json?.error || message;
    } catch {
      // keep default
    }
    throw new Error(message);
  }

  throw new Error('Downloaded report is not a valid PDF');
}

export interface ApiResponse<T = any> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
  [key: string]: any;
}

export interface VersionMetadata {
  apiVersion: string;
  baselineCodebaseVersion: string;
}

export interface CarbonTradingPortfolio {
  totalCredits: number;
  availableCredits: number;
  usedCredits: number;
  totalInvestment: number;
  averagePrice: number;
  lastPurchase: string | null;
}

export interface CarbonOffsetOption {
  _id: string;
  name: string;
  description: string;
  type: string;
  pricePerTon: number;
  availableCredits: number;
  verifiedBy: string;
  location:
    | string
    | {
        country?: string;
        state?: string;
        city?: string;
      };
  co2Reduction: number;
  rating: number;
}

export interface CarbonTradingTransaction {
  _id?: string;
  type: 'purchase' | 'offset' | 'transfer' | 'refund';
  amount: number;
  pricePerTon?: number;
  totalCost?: number;
  status: string;
  timestamp: string;
  offsetName?: string;
}

export interface CarbonMarketTrendPoint {
  date: string;
  price: number;
}

export interface MSMECarbonCreditsAccount {
  msmeId?: string;
  poolId?: string;
  allocatedCredits?: number;
  availableCredits?: number;
  usedCredits?: number;
  retiredCredits?: number;
  totalCO2Reduced?: number;
}

export interface CarbonCreditsPoolMarket {
  pool?: {
    currentPricePerCredit?: number;
    totalCreditsAvailable?: number;
    totalMSMEParticipants?: number;
    totalCO2Reduced?: number;
  };
  marketMetrics?: {
    averagePrice?: number;
    activeMSMEs?: number;
    totalVolume?: number;
  };
}

export interface CarbonCreditPoolTransaction {
  _id?: string;
  type?: string;
  creditsAmount?: number;
  pricePerCredit?: number;
  totalValue?: number;
  status?: string;
  createdAt?: string;
  description?: string;
}

class ApiService {
  private static readonly REPORTING_TIMEOUT_MS = 300_000;
  private static readonly CACHE_KEY = {
    version: buildApiCacheKey('GET', '/version'),
    profile: buildApiCacheKey('GET', '/msme/profile'),
    stats: buildApiCacheKey('GET', '/msme/stats'),
  };

  private buildQueryString(params: Record<string, QueryParamValue> = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  private async parseResponseBody(response: Response) {
    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers?.get?.('content-type') || '';
    const shouldTryJson =
      contentType.includes('application/json') || typeof response.json === 'function';

    if (shouldTryJson) {
      const parsedJson = await response.json().catch(() => null);
      if (parsedJson !== null) {
        return parsedJson;
      }
    }

    if (typeof response.text === 'function') {
      return response.text().catch(() => '');
    }

    return null;
  }

  private isRouteNotFoundError(err: unknown): boolean {
    return err instanceof Error && /route not found/i.test(err.message);
  }

  /** Try endpoints in order; on global 404 "Route not found", fall back to the next path. */
  private async requestFirstAvailable<T = ApiResponse>(endpoints: string[], options: RequestInit = {}): Promise<T> {
    let lastError: Error | null = null;
    for (let index = 0; index < endpoints.length; index += 1) {
      try {
        return await this.request<T>(endpoints[index], options);
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        const hasFallback = index < endpoints.length - 1;
        if (hasFallback && this.isRouteNotFoundError(err)) {
          continue;
        }
        throw lastError;
      }
    }
    throw lastError ?? new Error('Request failed');
  }

  private extractValidationMessages(responseBody: unknown): string[] {
    if (!responseBody || typeof responseBody !== 'object' || !Array.isArray((responseBody as { errors?: unknown }).errors)) {
      return [];
    }
    return (responseBody as { errors: unknown[] }).errors
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim();
        }
        if (entry && typeof entry === 'object') {
          const record = entry as { msg?: unknown; message?: unknown };
          if (typeof record.msg === 'string') {
            return record.msg.trim();
          }
          if (typeof record.message === 'string') {
            return record.message.trim();
          }
        }
        return '';
      })
      .filter((message) => message.length > 0);
  }

  private extractErrorMessage(responseBody: unknown, status: number, endpoint = '') {
    let baseMessage = '';

    if (
      responseBody &&
      typeof responseBody === 'object' &&
      'message' in responseBody &&
      typeof (responseBody as { message: unknown }).message === 'string'
    ) {
      baseMessage = (responseBody as { message: string }).message;
    } else if (
      responseBody &&
      typeof responseBody === 'object' &&
      'error' in responseBody &&
      typeof (responseBody as { error: unknown }).error === 'string'
    ) {
      baseMessage = (responseBody as { error: string }).error;
    } else if (typeof responseBody === 'string' && responseBody.trim().length > 0) {
      baseMessage = responseBody;
    }

    const validationMessages = this.extractValidationMessages(responseBody);
    if (validationMessages.length > 0) {
      if (!baseMessage || baseMessage === 'Validation errors') {
        return validationMessages.join(' ');
      }
      return `${baseMessage}: ${validationMessages.join(' ')}`;
    }

    if (baseMessage) {
      return baseMessage;
    }

    if (status === 429) {
      return 'Too many requests. Please wait a moment and try again.';
    }

    if (status === 502 || status === 503 || status === 504) {
      return reportingTimeoutMessage(endpoint);
    }

    return `HTTP error! status: ${status}`;
  }

  private resolveRequestTimeoutMs(endpoint: string, explicitTimeoutMs?: number) {
    if (explicitTimeoutMs != null) {
      return explicitTimeoutMs;
    }
    if (
      endpoint.startsWith('/reporting/')
      || endpoint.startsWith('/compliance-hub/')
      || endpoint.startsWith('/compliance/')
      || endpoint.includes('-orchestrate')
      || endpoint.startsWith('/ai-agents/')
      || endpoint.includes('/ai-analytics-engine')
      || endpoint.includes('import-accounting')
    ) {
      return ApiService.REPORTING_TIMEOUT_MS;
    }
    return undefined;
  }

  private buildFetchInit(options: RequestInit, timeoutMs?: number): RequestInit {
    const headers = buildSessionHeaders(
      options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : Array.isArray(options.headers)
          ? Object.fromEntries(options.headers)
          : (options.headers as Record<string, string> | undefined) ?? {}
    );

    const baseInit: RequestInit = {
      ...options,
      headers,
      credentials: 'include',
    };

    if (!timeoutMs || options.signal) {
      return baseInit;
    }
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return {
        ...baseInit,
        signal: AbortSignal.timeout(timeoutMs),
      };
    }
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      if (timeoutId && typeof timeoutId === 'object' && typeof timeoutId.unref === 'function') {
        timeoutId.unref();
      }
      return {
        ...baseInit,
        signal: controller.signal,
      };
    }
    return baseInit;
  }

  // Build an Error that carries the HTTP status. We expose it both as `status` and
  // as an axios-compatible `response.status` so that route guards / callers that were
  // written against the axios error shape (e.g. RequireMSME, RequireEnterprise) can
  // detect 401/403/404 instead of silently treating an expired session as a generic
  // failure (which left users stuck on a "retry" screen with no path to re-login).
  private createHttpError(responseBody: unknown, status: number, endpoint = ''): Error {
    const error = new Error(this.extractErrorMessage(responseBody, status, endpoint)) as Error & {
      status?: number;
      response?: { status: number; data?: unknown };
    };
    error.status = status;
    error.response = { status, data: responseBody };
    return error;
  }

  private parseContentDispositionFilename(header: string | null, fallback: string): string {
    if (!header) {
      return fallback;
    }
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }
    const match = /filename="?([^";]+)"?/i.exec(header);
    return match?.[1] || fallback;
  }

  async request<T = ApiResponse>(endpoint: string, options: RequestInit = {}, timeoutMs?: number): Promise<T> {
    const method = String(options.method || 'GET').toUpperCase();
    if (method === 'GET' && endpoint.split('?')[0] === '/auth/me') {
      return cachedApiGet(
        buildApiCacheKey('GET', '/auth/me'),
        API_CACHE_TTL.sessionMs,
        () => this.performRequest<T>(endpoint, options, timeoutMs)
      );
    }
    return this.performRequest<T>(endpoint, options, timeoutMs);
  }

  private async performRequest<T = ApiResponse>(endpoint: string, options: RequestInit = {}, timeoutMs?: number): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {};
    const resolvedTimeoutMs = this.resolveRequestTimeoutMs(endpoint, timeoutMs);

    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const hasContentTypeHeader = Object.keys(headers).some(
      (key) => key.toLowerCase() === 'content-type'
    );
    if (!(options.body instanceof FormData) && !hasContentTypeHeader) {
      headers['Content-Type'] = 'application/json';
    }

    const token = getClientAccessToken();
    const hasAuthorizationHeader = Object.keys(headers).some(
      (key) => key.toLowerCase() === 'authorization'
    );
    if (token && !hasAuthorizationHeader) {
      headers.Authorization = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(url, this.buildFetchInit({
        ...options,
        headers,
      }, resolvedTimeoutMs));
    } catch (err) {
      if (isLikelyTimeoutFailure(err)) {
        throw new Error(reportingTimeoutMessage(endpoint));
      }
      if (isLikelyNetworkFailure(err)) {
        throw new Error(
          'Unable to reach the server. Check your internet connection and that the API URL is configured correctly, then try again.'
        );
      }
      throw err instanceof Error ? err : new Error('Request failed');
    }

    const responseBody = await this.parseResponseBody(response);

    if (!response.ok) {
      throw this.createHttpError(responseBody, response.status, endpoint);
    }

    return responseBody as T;
  }

  private async pollReportPdfJob(jobId: string, timeoutMs: number): Promise<void> {
    const started = Date.now();
    const pollIntervalMs = 2000;
    const headers: Record<string, string> = {};
    const token = getClientAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    while (Date.now() - started < timeoutMs) {
      const statusResponse = await fetch(
        `${API_BASE_URL}/reporting/jobs/${jobId}`,
        this.buildFetchInit({ method: 'GET', headers }, timeoutMs)
      );
      const statusBody = await this.parseResponseBody(statusResponse);
      if (!statusResponse.ok) {
        throw this.createHttpError(statusBody, statusResponse.status, `/reporting/jobs/${jobId}`);
      }

      const status = statusBody?.data?.status;
      if (status === 'completed') {
        return;
      }
      if (status === 'failed') {
        throw new Error(statusBody?.data?.error || 'Report generation failed');
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, pollIntervalMs);
      });
    }

    throw new Error(reportingTimeoutMessage('/reporting/'));
  }

  private async requestBlob(
    endpoint: string,
    params: Record<string, QueryParamValue> = {},
    timeoutMs?: number
  ) {
    const useAsyncReporting = endpoint.startsWith('/reporting/');
    const queryParams = useAsyncReporting
      ? { ...params, async: '1' }
      : params;
    const url = `${API_BASE_URL}${endpoint}${this.buildQueryString(queryParams)}`;
    const headers: Record<string, string> = {};
    const token = getClientAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const resolvedTimeoutMs = this.resolveRequestTimeoutMs(endpoint, timeoutMs);

    let response: Response;
    try {
      response = await fetch(url, this.buildFetchInit({
        method: 'GET',
        headers,
      }, resolvedTimeoutMs));
    } catch (err) {
      if (isLikelyTimeoutFailure(err)) {
        throw new Error(reportingTimeoutMessage(endpoint));
      }
      if (isLikelyNetworkFailure(err)) {
        throw new Error(
          'Unable to reach the server. Check your internet connection and that the API URL is configured correctly, then try again.'
        );
      }
      throw err instanceof Error ? err : new Error('Request failed');
    }

    if (!response.ok) {
      const errorBody = await this.parseResponseBody(response);
      throw this.createHttpError(errorBody, response.status, endpoint);
    }

    if (response.status === 202 && useAsyncReporting) {
      const acceptedBody = await this.parseResponseBody(response);
      const jobId = acceptedBody?.data?.id;
      if (!jobId) {
        throw new Error('Report job was accepted but no job id was returned');
      }
      await this.pollReportPdfJob(jobId, resolvedTimeoutMs ?? ApiService.REPORTING_TIMEOUT_MS);
      const downloadResponse = await fetch(
        `${API_BASE_URL}/reporting/jobs/${jobId}/download`,
        this.buildFetchInit({ method: 'GET', headers }, resolvedTimeoutMs)
      );
      if (!downloadResponse.ok) {
        const downloadError = await this.parseResponseBody(downloadResponse);
        throw this.createHttpError(downloadError, downloadResponse.status, `/reporting/jobs/${jobId}/download`);
      }
      return ensurePdfBlob(downloadResponse);
    }

    const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
    if (contentType.includes('pdf') || String(params.format || '').toLowerCase() === 'pdf') {
      return ensurePdfBlob(response);
    }
    return response.blob();
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyLoginOtp(payload: { sessionId: string; emailOtp?: string; mobileOtp?: string }) {
    return this.request('/auth/login/verify-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async resendLoginOtp(payload: { email: string; password: string }) {
    return this.request('/auth/login/resend-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async verifyMfaLogin(payload: { mfaToken: string; code: string }) {
    return this.request('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async setupMfaEnrollment(payload: { enrollmentToken: string }) {
    return this.request('/auth/mfa/setup', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async enableMfaEnrollment(payload: { enrollmentToken: string; code: string }) {
    return this.request('/auth/mfa/enable', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async register(userData: any) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async requestRegistrationOtp(payload: { email: string; phone: string }) {
    return this.request('/auth/register/request-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async verifyRegistrationOtp(payload: { sessionId: string; otp?: string; emailOtp?: string; mobileOtp?: string }) {
    return this.request('/auth/register/verify-otp', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getVersionMetadata(): Promise<VersionMetadata> {
    return cachedApiGet(ApiService.CACHE_KEY.version, API_CACHE_TTL.versionMs, async () => {
      const response = await this.request<ApiResponse<VersionMetadata>>('/version');
      if (response?.data?.baselineCodebaseVersion && response?.data?.apiVersion) {
        return response.data;
      }

      throw new Error('Version metadata not available');
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateUserProfile(payload: { firstName?: string; lastName?: string; phone?: string }) {
    return this.request('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async changePassword(payload: { currentPassword: string; newPassword: string }) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async forgotPassword(userData: any) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async resetPassword(userData: { token: string; password: string }) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }



  async getOrganizationContext() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/organization/context'),
      API_CACHE_TTL.sessionMs,
      () => this.request('/organization/context')
    );
  }

  // MSME endpoints
  async getMSMEProfile() {
    return cachedApiGet(ApiService.CACHE_KEY.profile, API_CACHE_TTL.profileMs, () =>
      this.request('/msme/profile')
    );
  }

  async getRecommendations(params?: {
    status?: string;
    priority?: string;
    category?: string;
    page?: number;
    limit?: number;
  }) {
    return this.request(`/recommendations${this.buildQueryString({
      status: params?.status,
      priority: params?.priority,
      category: params?.category,
      page: params?.page,
      limit: params?.limit
    })}`);
  }

  async getElectricityDiscoms() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/msme/electricity-discoms'),
      API_CACHE_TTL.discomListMs,
      () => this.request('/msme/electricity-discoms')
    );
  }

  async updateMSMEProfile(data: any) {
    const result = await this.request('/msme/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    clearApiRequestCache(buildApiCacheKey('GET', '/organization/context'));
    return result;
  }

  async getReductionTarget() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/msme/reduction-target'),
      API_CACHE_TTL.profileMs,
      () => this.request('/msme/reduction-target')
    );
  }

  async updateReductionTarget(reductionTargetPct: number) {
    const result = await this.request('/msme/reduction-target', {
      method: 'PUT',
      body: JSON.stringify({ reductionTargetPct }),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async getMaintenanceUnitPrice() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/msme/maintenance-unit-price'),
      API_CACHE_TTL.profileMs,
      () => this.request('/msme/maintenance-unit-price')
    );
  }

  async updateMaintenanceUnitPrice(maintenanceUnitPriceInr: number) {
    const result = await this.request('/msme/maintenance-unit-price', {
      method: 'PUT',
      body: JSON.stringify({ maintenanceUnitPriceInr }),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async registerMSME(data: any) {
    const result = await this.request('/msme/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async getMSMEStats() {
    return cachedApiGet(ApiService.CACHE_KEY.stats, API_CACHE_TTL.statsMs, () =>
      this.request('/msme/stats')
    );
  }

  async getMSMEPaymentSummary() {
    return this.request('/msme/payment-summary');
  }

  async getManufacturingWorkflow() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/msme/manufacturing-workflow'),
      API_CACHE_TTL.profileMs,
      () => this.request('/msme/manufacturing-workflow')
    );
  }

  async getCarbonTwin(options: { periodDays?: number; runAgents?: boolean } = {}) {
    const periodDays = options.periodDays ?? 365;
    const runAgents = options.runAgents !== false;
    return this.request(
      `/msme/carbon-twin${this.buildQueryString({ periodDays, runAgents })}`
    );
  }

  async orchestrateCarbonTwin(data: { periodDays?: number; msmeId?: string } = {}) {
    return this.request('/ai-agents/carbon-twin-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async estimateManufacturingWorkflow(data: any) {
    const result = await this.request('/msme/manufacturing-workflow/estimate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async lockManufacturingWorkflow(isLocked: boolean) {
    const result = await this.request('/msme/manufacturing-workflow/lock', {
      method: 'POST',
      body: JSON.stringify({ isLocked }),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async generateOperationsTemplate(data?: { websiteUrl?: string }) {
    const result = await this.request('/msme/operations-template', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
    invalidateMsmeReadCache();
    return result;
  }

  /** Reads a sector plant setup. Does not change saved profile or workflow data. */
  async previewOperationsTemplate(data?: { websiteUrl?: string }) {
    return this.request('/msme/operations-template', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async requestGhgBoundaryGuidance() {
    return this.request('/msme/ghg-boundary-guidance', {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  // Carbon endpoints
  async getCarbonAssessment(params?: { page?: number; limit?: number; status?: string }) {
    const path = `/carbon/assessments${this.buildQueryString({
      page: params?.page,
      limit: params?.limit,
      status: params?.status
    })}`;
    const latestProbe = !params?.status
      && Number(params?.limit) === 1
      && (params?.page == null || Number(params.page) === 1);
    if (!latestProbe) {
      return this.request(path);
    }
    return cachedApiGet(
      buildApiCacheKey('GET', '/carbon/assessments', 'limit=1'),
      API_CACHE_TTL.carbonDashboardMs,
      () => this.request('/carbon/assessments?limit=1')
    );
  }

  async getCarbonAssessmentByPeriod(params?: {
    groupBy?: 'annual' | 'monthly' | 'weekly' | 'datewise';
    limit?: number;
  }) {
    const periodType = params?.groupBy === 'datewise' ? 'date_wise' : params?.groupBy;
    return this.request(`/carbon/document-bulk-assessment${this.buildQueryString({
      periodType,
      limit: params?.limit
    })}`);
  }

  async getDocumentBulkAssessment(params?: {
    periodType?: 'annual' | 'monthly' | 'weekly' | 'date_wise';
    startDate?: string;
    endDate?: string;
  }) {
    return this.request(`/carbon/document-bulk-assessment${this.buildQueryString({
      periodType: params?.periodType,
      startDate: params?.startDate,
      endDate: params?.endDate
    })}`);
  }

  async submitCarbonAssessment(data: any) {
    const result = await this.request('/carbon/assess', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateCarbonReadCache();
    invalidateMsmeReadCache();
    return result;
  }

  async getGranularCategoryEmissions(data?: {
    includeOrchestration?: boolean;
    includeBreakdown?: boolean;
    includeRawOrchestrationOutput?: boolean;
    includeSyntheticTransactions?: boolean;
  }) {
    return this.request('/carbon/granular-category-emissions', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getCarbonDashboard(periodType?: 'annual' | 'monthly' | 'weekly' | 'date_wise') {
    const queryString = this.buildQueryString({ periodType });
    const cacheKey = buildApiCacheKey('GET', '/carbon/dashboard', queryString.replace(/^\?/, ''));
    return cachedApiGet(cacheKey, API_CACHE_TTL.carbonDashboardMs, () =>
      this.request(`/carbon/dashboard${queryString}`)
    );
  }

  async getCarbonSavings(params?: { period?: 'monthly' | 'quarterly' | 'yearly'; includeTrends?: boolean; fresh?: boolean }) {
    const queryString = this.buildQueryString({
      period: params?.period,
      includeTrends: params?.includeTrends,
    });
    const cacheKey = buildApiCacheKey('GET', '/carbon/savings', queryString.replace(/^\?/, ''));
    if (params?.fresh) {
      clearApiRequestCache(cacheKey);
    }
    return cachedApiGet(cacheKey, API_CACHE_TTL.carbonSavingsMs, () =>
      this.request(`/carbon/savings${queryString}`)
    );
  }

  /** Multi-agent carbon analytics synthesis + chart-ready aggregates for dashboards. */
  async runCarbonAIAnalyticsEngine(body?: {
    transactions?: unknown[];
    inventoryScope?: 'all' | 'confirmed';
  }) {
    const sourceTransactions = body?.transactions;
    const slimTransactions = Array.isArray(sourceTransactions)
      ? sourceTransactions.slice(0, 150).map((row) => {
        const tx = row as {
          _id?: unknown;
          date?: unknown;
          category?: unknown;
          subcategory?: unknown;
          description?: unknown;
          amount?: unknown;
          carbonFootprint?: {
            co2Emissions?: unknown;
            screeningCo2Emissions?: unknown;
            emissionBreakdown?: unknown;
          };
          metadata?: { inventoryStatus?: unknown };
        };
        return {
          _id: tx?._id,
          date: tx?.date,
          category: tx?.category,
          subcategory: tx?.subcategory,
          description: typeof tx?.description === 'string' ? tx.description.slice(0, 180) : tx?.description,
          amount: tx?.amount,
          carbonFootprint: {
            co2Emissions: tx?.carbonFootprint?.co2Emissions,
            screeningCo2Emissions: tx?.carbonFootprint?.screeningCo2Emissions,
            emissionBreakdown: tx?.carbonFootprint?.emissionBreakdown,
          },
          metadata: tx?.metadata?.inventoryStatus
            ? { inventoryStatus: tx.metadata.inventoryStatus }
            : undefined,
        };
      })
      : undefined;
    return this.request('/carbon/ai-analytics-engine', {
      method: 'POST',
      body: JSON.stringify({
        ...(body || {}),
        ...(slimTransactions ? { transactions: slimTransactions } : {}),
      }),
    });
  }

  async getLedgerMonthlyEmissions(params?: { limit?: number; fresh?: boolean }) {
    const query = this.buildQueryString({ limit: params?.limit }).replace(/^\?/, '');
    const cacheKey = buildApiCacheKey('GET', '/carbon/ledger-monthly', query);
    if (params?.fresh) {
      clearApiRequestCache(cacheKey);
    }
    return cachedApiGet(
      cacheKey,
      API_CACHE_TTL.carbonDashboardMs,
      () => this.request(`/carbon/ledger-monthly${query ? `?${query}` : ''}`)
    );
  }

  async implementRecommendation(assessmentId: string, recommendationIndex: number) {
    const result = await this.request('/carbon/recommendations/implement', {
      method: 'PUT',
      body: JSON.stringify({ assessmentId, recommendationIndex }),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async selectMitigationAction(params: {
    assessmentId: string;
    recommendationIndex: number;
    actionId: string;
    action?: {
      label: string;
      implementationTrack?: string;
      estimatedImpactKg?: number | null;
    };
    selected?: boolean;
  }) {
    const result = await this.request('/carbon/recommendations/actions', {
      method: 'PUT',
      body: JSON.stringify(params),
    });
    invalidateMsmeReadCache();
    return result;
  }

  // Fuel prices (official PPAC source)
  async getFuelPrices(params?: { location?: string; days?: number; refresh?: boolean }) {
    return this.request(`/fuel-prices${this.buildQueryString({
      location: params?.location,
      days: params?.days,
      refresh: params?.refresh ? true : undefined
    })}`);
  }

  async getCarbonForecast(params?: {
    forecastPeriods?: number;
    modelType?: string;
    confidenceLevel?: number;
  }) {
    const query = this.buildQueryString({
      forecastPeriods: params?.forecastPeriods,
      modelType: params?.modelType,
      confidenceLevel: params?.confidenceLevel
    }).replace(/^\?/, '');
    return cachedApiGet(
      buildApiCacheKey('GET', '/carbon-forecasting/forecast', query),
      API_CACHE_TTL.statsMs,
      () => this.request(`/carbon-forecasting/forecast${query ? `?${query}` : ''}`)
    );
  }

  // Bank endpoints
  async getBanks() {
    return this.request('/banks');
  }

  async getBank(id: string) {
    return this.request(`/banks/${id}`);
  }

  async getBankLoans(bankId: string, params?: { status?: string; page?: number; limit?: number }) {
    return this.request(`/banks/${bankId}/loans${this.buildQueryString({
      status: params?.status,
      page: params?.page,
      limit: params?.limit
    })}`);
  }

  async getBankStatistics(bankId: string) {
    return this.request(`/banks/${bankId}/statistics`);
  }

  // Green Loan endpoints
  async checkLoanEligibility(data: any) {
    return this.request('/green-loans/eligibility-check', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async applyForLoan(data: any) {
    const result = await this.request('/green-loans/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/green-loans/my-loans'));
    return result;
  }

  async getMyLoans(params?: { status?: string; page?: number; limit?: number }) {
    const query = this.buildQueryString({
      status: params?.status,
      page: params?.page,
      limit: params?.limit
    }).replace(/^\?/, '');
    return cachedApiGet(
      buildApiCacheKey('GET', '/green-loans/my-loans', query),
      API_CACHE_TTL.sessionMs,
      () => this.request(`/green-loans/my-loans${query ? `?${query}` : ''}`)
    );
  }

  async getLoanDetails(loanId: string) {
    return this.request(`/green-loans/${loanId}`);
  }

  async updateLoanStatus(loanId: string, data: any) {
    const result = await this.request(`/green-loans/${loanId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/green-loans/my-loans'));
    return result;
  }

  // Transaction endpoints
  async getUncertaintyClarifications() {
    return this.request('/transactions/uncertainty-clarifications');
  }

  async getTransactions(params?: {
    page?: number;
    limit?: number;
    category?: string;
    source?: string;
    highValueBillPending?: boolean;
    sortBy?: 'createdAt' | 'date' | 'amount' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
    search?: string;
    includeReportCompleteness?: boolean;
  }) {
    const query = this.buildQueryString({
      page: params?.page,
      limit: params?.limit,
      category: params?.category,
      source: params?.source,
      highValueBillPending: params?.highValueBillPending === true ? 'true' : undefined,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      search: params?.search || undefined,
      includeReportCompleteness: params?.includeReportCompleteness === true ? 'true' : undefined
    });
    // ttl 0 keeps a completed list fresh and still collapses overlapping identical calls.
    return cachedApiGet(buildApiCacheKey('GET', '/transactions', query.replace(/^\?/, '')), 0, () =>
      this.request(`/transactions${query}`)
    );
  }

  async downloadTransactionAuditWorkbook(params?: {
    startDate?: string;
    endDate?: string;
    source?: string;
    category?: string;
    label?: string;
  }) {
    const blob = await this.requestBlob('/transactions/audit-workbook', {
      startDate: params?.startDate,
      endDate: params?.endDate,
      source: params?.source && params.source !== 'all' ? params.source : undefined,
      category: params?.category,
      label: params?.label,
    });
    return {
      blob,
      fileName: `transaction_audit_${new Date().toISOString().slice(0, 10)}.xlsx`,
    };
  }

  async downloadUploadAuditWorkbook(batchId: string) {
    const blob = await this.requestBlob('/transactions/audit-workbook/upload', { batchId });
    return {
      blob,
      fileName: `transaction_audit_upload_${new Date().toISOString().slice(0, 10)}.xlsx`,
    };
  }

  async downloadConnectorAuditWorkbook(providerId: string) {
    const blob = await this.requestBlob('/transactions/audit-workbook/connector', { providerId });
    return {
      blob,
      fileName: `transaction_audit_${providerId}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    };
  }

  async getSpamTransactions(params?: {
    page?: number;
    limit?: number;
    source?: string;
    startDate?: string;
    endDate?: string;
  }) {
    return this.request(`/transactions/spam${this.buildQueryString({
      page: params?.page,
      limit: params?.limit,
      source: params?.source,
      startDate: params?.startDate,
      endDate: params?.endDate,
    })}`);
  }

  async getAdminEmissionFactorReview(cycle?: string) {
    const query = new URLSearchParams();
    if (cycle) query.set('cycle', cycle);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/admin/emission-factors/review${suffix}`);
  }

  async proposeAdminEmissionFactorReview(data: { cycle?: string; updates?: Array<Record<string, unknown>> }) {
    return this.request('/admin/emission-factors/review/propose', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async reviewAdminEmissionFactors(data: {
    cycle: string;
    decision: 'approve' | 'reject';
    factorIds?: string[];
    notes?: string;
  }) {
    return this.request('/admin/emission-factors/review/decisions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async publishAdminEmissionFactorReview(cycle: string) {
    return this.request('/admin/emission-factors/review/publish', {
      method: 'POST',
      body: JSON.stringify({ cycle }),
    });
  }

  async getAdminSystemHealth(params?: { limit?: number }) {
    return this.request(`/admin/system-health${this.buildQueryString({
      limit: params?.limit,
    })}`);
  }

  async getOrchestrationStatus() {
    return this.request('/orchestration-manager/status');
  }

  async getOrchestrationEvents(params?: { limit?: number }) {
    return this.request(`/orchestration-manager/events${this.buildQueryString({
      limit: params?.limit,
    })}`);
  }

  async getIntelligenceHubOverview(params?: { includeAgentStatus?: boolean }) {
    return this.request(`/intelligence-hub/overview${this.buildQueryString({
      includeAgentStatus: params?.includeAgentStatus,
    })}`);
  }

  async getIntelligenceHubModels() {
    return this.request('/intelligence-hub/models');
  }

  async queryIntelligenceHub(query: string, context?: Record<string, unknown>) {
    return this.request('/intelligence-hub/query', {
      method: 'POST',
      body: JSON.stringify({ query, context }),
    });
  }

  async analyzeIntelligenceHub(params?: {
    msmeId?: string;
    dimensions?: string[];
    query?: string;
    transactions?: unknown[];
    context?: Record<string, unknown>;
    options?: Record<string, unknown>;
  }) {
    return this.request('/intelligence-hub/analyze', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  async getIntelligenceHubPlan(context?: Record<string, unknown>) {
    return this.request('/intelligence-hub/plan', {
      method: 'POST',
      body: JSON.stringify({ context: context || {} }),
    });
  }

  async createTransaction(data: any) {
    return this.request('/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async classifyTransactionScope(id: string, answers: Record<string, string | number>) {
    return this.request(`/transactions/${id}/scope-classification`, {
      method: 'PUT',
      body: JSON.stringify({ answers }),
    });
  }

  async updateTransaction(id: string, data: any) {
    return this.request(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async crossCheckTransactionBill(id: string, documentId: string) {
    return this.request(`/transactions/${id}/bill-cross-check`, {
      method: 'POST',
      body: JSON.stringify({ documentId }),
    });
  }

  async classifyTransactions(data: {
    transactionIds: string[];
    emissionBoundary: 'company' | 'product';
    productNames?: string[];
    reason?: string;
  }) {
    return this.request('/transactions/classify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTransactionProductCatalog() {
    return cachedApiGet(buildApiCacheKey('GET', '/transactions/product-catalog'), 0, () =>
      this.request('/transactions/product-catalog')
    );
  }

  async getProductPcfSetup() {
    return this.request('/product-pcf/setup');
  }

  async saveProductPcfSetup(data: Record<string, unknown>) {
    return this.request('/product-pcf/setup', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async completeProductPcfSetup() {
    return this.request('/product-pcf/setup/complete', {
      method: 'POST',
    });
  }

  async getUnifiedProductPcf() {
    return this.request('/product-pcf/unified');
  }

  async getProductLcaStudies() {
    return this.request('/product-pcf/lca/studies');
  }

  async getProductLcaReport() {
    return this.request('/product-pcf/lca/report');
  }

  async downloadProductLcaReportPdf() {
    const blob = await this.requestBlob('/product-pcf/lca/report', { format: 'pdf' });
    return {
      blob,
      fileName: `product-lca-report_${new Date().toISOString().slice(0, 10)}.pdf`,
    };
  }

  async syncProductPcfFromGhgBoundaries(refreshHub = true) {
    return this.request('/product-pcf/sync-ghg-boundaries', {
      method: 'POST',
      body: JSON.stringify({ refreshHub }),
    });
  }

  async importProductEpd(epd: Record<string, unknown> | Record<string, unknown>[]) {
    return this.request('/product-pcf/lca/epd-import', {
      method: 'POST',
      body: JSON.stringify(Array.isArray(epd) ? { epds: epd } : { epd }),
    });
  }

  async autoClassifyProductPcf(data?: {
    transactionIds?: string[];
    confidenceThreshold?: number;
    dryRun?: boolean;
    runProfiler?: boolean;
  }) {
    return this.request('/product-pcf/auto-classify', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateGeotaggedEvidence(data: {
    documentId?: string;
    sourceWorkflow: string;
    period?: string;
    applyChanges?: boolean;
    runAgents?: boolean;
  }) {
    return this.request('/ai-agents/geotagged-evidence-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async orchestrateReadinessActions(data?: {
    period?: string;
    actionLimit?: number;
    runFullOrchestration?: boolean;
  }) {
    return this.request('/ai-agents/readiness-actions-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateReportingReadiness(data?: {
    period?: string;
    actionLimit?: number;
    includeBuyerOrchestration?: boolean;
    includeInventoryReview?: boolean;
    runAutopilot?: boolean;
    includeBrsrOrchestration?: boolean;
    includeIsoOrchestration?: boolean;
    includePcfOrchestration?: boolean;
    includeCbamOrchestration?: boolean;
    includeCertificationOrchestration?: boolean;
    includeHeavyStages?: boolean;
  }) {
    return this.request('/ai-agents/reporting-readiness-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateAllReports(data?: {
    period?: string;
    cbamPeriod?: string;
    iso14067Period?: string;
    profile?: 'light' | 'standard' | 'full';
    runWorkflows?: boolean;
    persistResults?: boolean;
    actionLimit?: number;
    includeBuyerOrchestration?: boolean;
    includeInventoryReview?: boolean;
    runAutopilot?: boolean;
    includeBrsrOrchestration?: boolean;
    includeIsoOrchestration?: boolean;
    includePcfOrchestration?: boolean;
    includeCbamOrchestration?: boolean;
    includeFullReports?: boolean;
  }) {
    return this.request('/reporting/orchestrate-all', {
      method: 'POST',
      body: JSON.stringify({
        profile: 'standard',
        runWorkflows: false,
        ...data,
      }),
    });
  }

  async orchestrateReportStage(data: {
    stage: string;
    period?: string;
    cbamPeriod?: string;
    iso14067Period?: string;
    profile?: 'light' | 'standard' | 'full';
    runWorkflows?: boolean;
    persistResults?: boolean;
    actionLimit?: number;
    includeBuyerOrchestration?: boolean;
    includeInventoryReview?: boolean;
    runAutopilot?: boolean;
    includeBrsrOrchestration?: boolean;
    includeIsoOrchestration?: boolean;
    includePcfOrchestration?: boolean;
    includeCbamOrchestration?: boolean;
    accumulated?: Record<string, unknown>;
  }) {
    return this.request('/reporting/orchestrate/stage', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async orchestrateIsoAssurance(data?: {
    period?: string;
    include14067?: boolean;
  }) {
    return this.request('/ai-agents/iso-assurance-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateBrsrDisclosure(data?: { period?: string }) {
    return this.request('/ai-agents/brsr-disclosure-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateCbamReadiness(data?: {
    period?: string;
    applyChanges?: boolean;
  }) {
    return this.request('/ai-agents/cbam-readiness-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateEprReadiness(data?: {
    period?: string;
    applyChanges?: boolean;
  }) {
    return this.request('/ai-agents/epr-readiness-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async resolveApplicationWarnings(data?: {
    warnings?: Array<{
      type?: string;
      message?: string;
      action?: string;
      detail?: string;
      documentId?: string;
    }>;
    period?: string;
    documentId?: string;
    applyChanges?: boolean;
  }) {
    return this.request('/ai-agents/resolve-warnings', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateProductPcf(data?: {
    transactionIds?: string[];
    confidenceThreshold?: number;
    dryRun?: boolean;
    includeUnifiedView?: boolean;
    useFullOrchestration?: boolean;
    recalculateEmissions?: boolean;
    runAttribution?: boolean;
    persistResults?: boolean;
    period?: string;
  }) {
    return this.request('/ai-agents/product-pcf-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async analyzeProductPcfFeed(data?: {
    transactionIds?: string[];
    confidenceThreshold?: number;
    dryRun?: boolean;
    recalculateEmissions?: boolean;
    runAttribution?: boolean;
    persistResults?: boolean;
    period?: string;
  }) {
    return this.request('/ai-agents/product-pcf-analyze', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getProductPcfExceptions(params?: { page?: number; limit?: number; types?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.types) query.set('types', params.types);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/product-pcf/exceptions${suffix}`);
  }

  async deleteTransaction(id: string) {
    return this.request(`/transactions/${id}`, {
      method: 'DELETE',
    });
  }

  async getAccountingConnectors() {
    return this.request('/transactions/accounting/connectors');
  }

  async getLatestAccountingImport() {
    return this.requestFirstAvailable([
      '/transactions/latest-accounting-import',
      '/accounting/latest-accounting-import',
    ]);
  }

  async importAccountingTransactions(data: {
    provider: string;
    transactions: unknown[];
    runAgents?: boolean;
    runOrchestration?: boolean;
    awaitFileConfirmation?: boolean;
    fileName?: string;
  }) {
    return this.requestFirstAvailable([
      '/transactions/import-accounting',
      '/accounting/import-accounting',
    ], {
      method: 'POST',
      body: JSON.stringify({
        runAgents: data.runAgents !== false,
        runOrchestration: data.runOrchestration !== false,
        provider: data.provider,
        transactions: data.transactions,
        awaitFileConfirmation: data.awaitFileConfirmation === true,
        fileName: data.fileName,
      }),
    });
  }

  async confirmAccountingImportFile(batchId: string) {
    return this.requestFirstAvailable([
      '/transactions/import-accounting-file/confirm',
      '/accounting/import-accounting-file/confirm',
    ], {
      method: 'POST',
      body: JSON.stringify({
        batchId,
        runAgents: true,
        runOrchestration: true,
      }),
    });
  }

  async rejectAccountingImportFile(batchId: string) {
    return this.requestFirstAvailable([
      '/transactions/import-accounting-file/reject',
      '/accounting/import-accounting-file/reject',
    ], {
      method: 'POST',
      body: JSON.stringify({ batchId }),
    });
  }

  async importAccountingFile(
    provider: string,
    file: File,
    options?: { runAgents?: boolean; runOrchestration?: boolean }
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', provider);
    if (options?.runAgents === false) {
      formData.append('runAgents', 'false');
    }
    if (options?.runOrchestration === false) {
      formData.append('runOrchestration', 'false');
    }

    try {
      return await this.requestFirstAvailable([
        '/transactions/import-accounting-file',
        '/accounting/import-accounting-file',
      ], {
        method: 'POST',
        body: formData,
      });
    } catch (err) {
      if (!this.isRouteNotFoundError(err)) {
        throw err;
      }

      const transactions = await parseAccountingImportFileClientSide(file);
      return this.importAccountingTransactions({
        provider,
        transactions,
        runAgents: options?.runAgents,
        runOrchestration: options?.runOrchestration,
        awaitFileConfirmation: true,
        fileName: file.name,
      });
    }
  }

  async getAccountingConnectorStatus(provider: string) {
    return this.request(`/transactions/accounting/${provider}/status`);
  }

  async syncAccountingTransactions(provider: string, data?: Record<string, unknown>) {
    return this.request(`/transactions/accounting/${provider}/sync`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async previewAccountingTransactions(provider: string, data?: Record<string, unknown>) {
    return this.request(`/transactions/accounting/${provider}/preview`, {
      method: 'POST',
      body: JSON.stringify(data || { syncAllPages: true }),
    });
  }

  async getAccountingApiStatus() {
    return this.request('/transactions/accounting/api-status');
  }

  async getMsmeConnectorConnections() {
    return this.request('/transactions/accounting/connections');
  }

  async connectMsmeAccountingProvider(
    provider: string,
    data: { connectionType?: 'api' | 'import'; credentials?: Record<string, unknown> }
  ) {
    return this.request(`/transactions/accounting/connections/${provider}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async disconnectMsmeAccountingProvider(provider: string) {
    return this.request(`/transactions/accounting/connections/${provider}`, {
      method: 'DELETE',
    });
  }

  async testMsmeAccountingConnection(
    provider: string,
    credentials?: Record<string, unknown>
  ) {
    return this.request(`/transactions/accounting/connections/${provider}/test`, {
      method: 'POST',
      body: JSON.stringify(credentials ? { credentials } : {}),
    });
  }

  async syncAccountingConnectorsForCarbon(options?: {
    runAgents?: boolean;
    runOrchestration?: boolean;
    syncAllPages?: boolean;
  }) {
    return this.request('/transactions/accounting/sync-connectors', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }

  // Incentives endpoints
  private mapIncentiveRewards(rewards: unknown[]) {
    return (Array.isArray(rewards) ? rewards : []).map((reward) => {
      const entry = reward as {
        rewardId?: string;
        id?: string;
        title?: string;
        description?: string;
        cost?: number;
        category?: string;
        available?: boolean;
      };
      return {
        id: entry.rewardId ?? entry.id ?? '',
        title: entry.title ?? '',
        description: entry.description ?? '',
        cost: entry.cost ?? 0,
        category: entry.category ?? 'general',
        available: entry.available !== false,
      };
    });
  }

  private unwrapApiData<T>(response: unknown): T {
    if (response && typeof response === 'object' && 'data' in response) {
      return (response as ApiResponse<T>).data as T;
    }
    return response as T;
  }

  private async getIncentivesFromLegacyRoutes(): Promise<ApiResponse> {
    const [statsResponse, dailyTasksResponse, leaderboardResponse] = await Promise.all([
      this.request('/incentives/stats'),
      this.request('/incentives/daily-tasks'),
      this.request('/incentives/leaderboard'),
    ]);

    const statsPayload = this.unwrapApiData<Record<string, unknown>>(statsResponse);
    const rewards = Array.isArray(statsPayload?.rewards) ? statsPayload.rewards : [];

    return {
      success: true,
      data: {
        incentives: this.mapIncentiveRewards(rewards),
        userStats: statsPayload?.userStats,
        achievements: statsPayload?.achievements,
        rewards,
        dailyTasks: this.unwrapApiData<unknown[]>(dailyTasksResponse) ?? [],
        leaderboard: this.unwrapApiData<unknown[]>(leaderboardResponse) ?? [],
      },
    };
  }

  private async getFinanceOverviewFromLegacySignals(): Promise<ApiResponse> {
    const [profileResponse, incentivesResponse] = await Promise.all([
      this.getMSMEProfile().catch(() => null),
      this.getIncentives().catch(() => null),
    ]);

    const profile = this.unwrapApiData<Record<string, unknown>>(profileResponse);
    const incentivesData = this.unwrapApiData<Record<string, unknown>>(incentivesResponse);
    const achievements = Array.isArray(incentivesData?.achievements)
      ? (incentivesData.achievements as Array<{ id?: string; progress?: number }>)
      : [];
    const userStats = (incentivesData?.userStats ?? {}) as Record<string, unknown>;
    const assessmentAchievement = achievements.find((item) => item.id === 'achv_assessments');
    const recommendationAchievement = achievements.find((item) => item.id === 'achv_reco');
    const assessmentCount = Number(assessmentAchievement?.progress ?? 0);
    const implementedRecommendations = Number(recommendationAchievement?.progress ?? 0);
    const carbonSavedKg = Number(userStats.carbonSaved ?? 0);
    const latestCarbonScore = Number(profile?.carbonScore ?? 0);

    const buildOption = (
      id: string,
      title: string,
      description: string,
      path: string,
      ctaLabel: string
    ) => ({
      id,
      title,
      description,
      path,
      category: 'finance',
      isEligible: false,
      status: 'in_progress',
      criteria: [],
      ctaLabel,
    });

    const options = [
      buildOption(
        'adeetie',
        'ADEETIE (BEE)',
        'Interest support for energy-efficient equipment. Apply on the official BEE site.',
        'https://adeetie.beeindia.gov.in/',
        'Open official site'
      ),
      buildOption(
        'pm_kusum',
        'PM-KUSUM',
        'MNRE support for solar pumps and grid-connected solar. Apply on the official site.',
        'https://pmkusum.mnre.gov.in/',
        'Open official site'
      ),
      buildOption(
        'zed',
        'MSME ZED',
        'Zero Defect Zero Effect certification and subsidy for eligible MSMEs.',
        'https://zed.msme.gov.in/',
        'Open official site'
      ),
      {
        id: 'government_schemes',
        title: 'Government schemes',
        description: 'ADEETIE, MSME ZED, BEE, PM-KUSUM, and the MSME scheme list. Applications stay on the official sites.',
        path: '/carbon-savings?section=schemes',
        category: 'workflow',
        isEligible: true,
        status: 'in_progress',
        criteria: [],
        ctaLabel: 'See government schemes',
      },
    ];

    const nextSteps = [];
    if (assessmentCount <= 0) {
      nextSteps.push({
        id: 'run_assessment',
        label: 'Run a carbon assessment',
        description: 'A saved assessment is useful evidence when a government portal asks for emissions progress.',
        path: '/carbon-footprint?section=start-assessment',
      });
    }
    if (implementedRecommendations < 3) {
      nextSteps.push({
        id: 'implement_reco',
        label: 'Implement sustainability recommendations',
        description: 'Completed reduction actions are useful evidence on government scheme sites.',
        path: '/carbon-footprint?section=recommendations',
      });
    }

    return {
      success: true,
      data: {
        companyName: String(profile?.companyName ?? 'Your company'),
        emissionsAchievements: {
          assessmentCount,
          implementedRecommendations,
          energyRecommendationsImplemented: 0,
          latestCarbonScore: Number.isFinite(latestCarbonScore) && latestCarbonScore > 0
            ? latestCarbonScore
            : null,
          absoluteReductionKg: 0,
          reductionPercent: 0,
          carbonSavedKg,
          savingsPercent: 0,
          reductionTargetPct: null,
          targetProgressPercent: null,
          hasAssessment: assessmentCount > 0,
          hasReductionHistory: false,
        },
        summary: {
          eligibleCount: 0,
          inProgressCount: options.length - 1,
          totalOptions: options.length - 1,
          headline:
            'Explore finance and incentive options below. Full eligibility scoring will appear after the workspace API syncs.',
        },
        options,
        nextSteps: nextSteps.slice(0, 4),
      },
    };
  }

  async getIncentives() {
    try {
      return await this.request('/incentives');
    } catch (err) {
      if (!this.isRouteNotFoundError(err)) {
        throw err;
      }
      return this.getIncentivesFromLegacyRoutes();
    }
  }

  async getFinanceOverview() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/finance/overview'),
      API_CACHE_TTL.statsMs,
      async () => {
        try {
          return await this.requestFirstAvailable([
            '/incentives/finance-overview',
            '/finance/overview',
          ]);
        } catch (err) {
          if (!this.isRouteNotFoundError(err)) {
            throw err;
          }
          return this.getFinanceOverviewFromLegacySignals();
        }
      }
    );
  }

  async claimIncentive(incentiveId: string) {
    return this.request(`/incentives/${incentiveId}/claim`, {
      method: 'POST',
    });
  }

  async completeDailyTask(taskId: string) {
    return this.request(`/incentives/daily-tasks/${taskId}/complete`, {
      method: 'POST',
    });
  }

  async getAdeetieDprPdf() {
    return this.requestBlob('/adeetie/dpr-brief/pdf');
  }

  async getCarbonPassport() {
    return this.request('/carbon-passport');
  }

  async enableCarbonPassport() {
    return this.request('/carbon-passport/enable', { method: 'POST' });
  }

  async getPublicCarbonPassport(token: string) {
    return this.request(`/carbon-passport/public/${token}`);
  }

  async askCarbonCopilot(message: string) {
    return this.request('/carbon-copilot/ask', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async getSupplierEnrollmentInvite(token: string) {
    return this.request(`/supplier-enrollment/${encodeURIComponent(token)}`);
  }

  async acceptSupplierEnrollmentInvite(token: string) {
    return this.request(`/supplier-enrollment/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getIngestPlatformFeatures() {
    return this.request('/ingest/features');
  }

  async createUpiDocumentSession(amountInr?: number) {
    return this.request('/ingest/upi/sessions', {
      method: 'POST',
      body: JSON.stringify(amountInr ? { amountInr } : {}),
    });
  }

  async confirmUpiDocumentPayment(sessionId: string, upiReference: string, payerVpa?: string) {
    return this.request(`/ingest/upi/sessions/${sessionId}/confirm-payment`, {
      method: 'POST',
      body: JSON.stringify({ upiReference, payerVpa }),
    });
  }

  async uploadUpiDocument(sessionId: string, file: File) {
    const form = new FormData();
    form.append('document', file);
    return this.request(`/ingest/upi/sessions/${sessionId}/upload`, {
      method: 'POST',
      body: form,
    });
  }

  async uploadWhatsappDocument(file: File) {
    const form = new FormData();
    form.append('document', file);
    return this.request('/ingest/whatsapp/upload', {
      method: 'POST',
      body: form,
    });
  }

  async getDecarbonisationScore() {
    return this.request('/green-finance/decarbonisation-score');
  }

  async getPmKusumEligibility(plantCapacityKw?: number) {
    return this.request(`/green-finance/pm-kusum/eligibility${this.buildQueryString({
      plantCapacityKw,
    })}`);
  }

  async getSidbiEligibility() {
    return this.request('/green-finance/sidbi/eligibility');
  }

  async getGreenLoanProjectBrief(measureType?: string) {
    return this.request(`/green-finance/project-brief${this.buildQueryString({ measureType })}`);
  }

  async getPatAbatementCurve(plantScale?: number) {
    return this.request(`/green-finance/pat-abatement-curve${this.buildQueryString({ plantScale })}`);
  }

  async getGreenHydrogenScenarios(params?: {
    electrolyzerCapacityMw?: number;
    capacityFactorPercent?: number;
    renewablePpaSharePercent?: number;
    gridTariffInrPerKwh?: number;
    renewablePpaTariffInrPerKwh?: number;
    waterCostInrPerKl?: number;
  }) {
    return this.request(`/green-finance/green-hydrogen/scenarios${this.buildQueryString(params)}`);
  }

  async getCbamDeclarationDraft(params?: { period?: string }) {
    return this.request(`/reporting/cbam${this.buildQueryString({
      period: params?.period,
      format: 'declaration',
    })}`);
  }

  // Reporting endpoints
  async getCbamReport(params?: { period?: string }) {
    return this.request(`/reporting/cbam${this.buildQueryString({
      period: params?.period
    })}`);
  }

  async getISO14064Report(params?: { period?: string; baseYear?: number }) {
    return this.request(`/reporting/iso-14064${this.buildQueryString({
      period: params?.period,
      baseYear: params?.baseYear
    })}`);
  }

  async getISO14067Report(params?: { period?: string; functionalUnit?: string; allocationMethod?: string }) {
    return this.request(`/reporting/iso-14067${this.buildQueryString({
      period: params?.period,
      functionalUnit: params?.functionalUnit,
      allocationMethod: params?.allocationMethod
    })}`);
  }

  async getIsoGapClosureChecklist(params?: { period?: string; framework?: 'all' | 'iso14064' | 'iso14067' }) {
    return this.request(`/reporting/iso-gap-closure${this.buildQueryString({
      period: params?.period,
      framework: params?.framework
    })}`);
  }

  async getBrsrGapClosureChecklist(params?: { period?: string }) {
    return this.request(`/reporting/brsr-gap-closure${this.buildQueryString({
      period: params?.period
    })}`);
  }

  async getCbamGapClosureChecklist(params?: { period?: string }) {
    return this.request(`/reporting/cbam-gap-closure${this.buildQueryString({
      period: params?.period
    })}`);
  }

  async getEprGapClosureChecklist(params?: { period?: string }) {
    return this.request(`/reporting/epr-gap-closure${this.buildQueryString({
      period: params?.period
    })}`);
  }

  async updateEprProfile(data: Record<string, unknown>) {
    return this.request('/compliance-hub/epr-profile', {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }

  async getThirdPartyVerifierWorkflow(params?: { period?: string; frameworks?: string[] }) {
    return this.request(
      `/compliance-hub/third-party-verifier-workflow${this.buildQueryString({
        period: params?.period,
        frameworks: params?.frameworks?.join(','),
      })}`
    );
  }

  async createThirdPartyVerifierRequest(data: {
    framework: string;
    verifierName: string;
    verifierEmail: string;
    verifierAccreditation?: string;
    assuranceLevel?: 'limited' | 'reasonable';
    notes?: string;
  }) {
    const result = await this.request('/compliance-hub/third-party-verifier/request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async updateThirdPartyVerifierRequest(requestId: string, data: {
    status?: string;
    statementDocumentId?: string;
    notes?: string;
    statementReceipt?: {
      documentUrl?: string;
      documentName?: string;
      assuranceLevel?: 'limited' | 'reasonable';
      verifierAccreditationId?: string;
      statementPeriodStart?: string;
      statementPeriodEnd?: string;
    };
    documentUrl?: string;
    documentName?: string;
    assuranceLevel?: 'limited' | 'reasonable';
    verifierAccreditationId?: string;
    statementPeriodStart?: string;
    statementPeriodEnd?: string;
  }) {
    const result = await this.request(`/compliance-hub/third-party-verifier/${requestId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async orchestrateThirdPartyVerifier(data?: { period?: string; frameworks?: string[] }) {
    return this.request('/ai-agents/third-party-verifier-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async orchestrateActivityDataUplift(data?: { period?: string; applyScope3Changes?: boolean }) {
    return this.request('/ai-agents/activity-data-uplift-orchestrate', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getScope3ReclassificationProposals(params?: { period?: string; minConfidence?: number }) {
    return this.request(`/reporting/scope3-reclassification-proposals${this.buildQueryString({
      period: params?.period,
      minConfidence: params?.minConfidence,
    })}`);
  }

  async applyScope3Reclassifications(data?: {
    period?: string;
    confirm?: boolean;
    minConfidence?: number;
    transactionIds?: string[];
    applyAllUnclassified?: boolean;
  }) {
    return this.request('/reporting/scope3-reclassification-apply', {
      method: 'POST',
      body: JSON.stringify({
        confirm: true,
        applyAllUnclassified: true,
        ...(data || {}),
      }),
    });
  }

  async getGhgInventoryAuditLog(params?: { page?: number; limit?: number }) {
    return this.request(`/reporting/ghg-inventory-audit-log${this.buildQueryString({
      page: params?.page,
      limit: params?.limit
    })}`);
  }

  async updateRecalculationPolicy(data: { policyStatement?: string; triggers?: string[] }) {
    const result = await this.request('/compliance-hub/recalculation-policy', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async updateInventoryAccountability(data: {
    inventoryOwner?: string;
    evidenceRetentionYears?: number | null;
  }) {
    const result = await this.request('/compliance-hub/inventory-accountability', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async runMsmeAdvisory(params?: {
    msmeId?: string;
    signupGoal?: string;
    period?: string;
    includeAuditPack?: boolean;
    processingFlags?: Record<string, unknown>;
  }) {
    return this.request('/ai-agents/msme-advisory', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  async getSegmentParityAnalysis() {
    return this.request('/ai-agents/segment-parity');
  }

  async automateISOCertification(data: {
    transactions: any[];
    msmeId?: string;
    msmeData?: any;
    documents?: any[];
    behaviorOverrides?: Record<string, any>;
    contextOverrides?: Record<string, any>;
    frameworks?: {
      iso14064?: Record<string, any>;
      iso14067?: Record<string, any>;
    };
  }) {
    return this.request('/ai-agents/automate-iso-certification', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReportingCarbonFootprint(params?: { period?: string }) {
    return this.request(`/reporting/carbon-footprint${this.buildQueryString({
      period: params?.period,
    })}`);
  }

  async getReportingTrends(params?: { period?: string }) {
    return this.request(`/reporting/trends${this.buildQueryString({
      period: params?.period,
    })}`);
  }

  async getReportingComparisons(params?: { period?: string }) {
    const query = params?.period ? `?period=${encodeURIComponent(params.period)}` : '';
    return this.request(`/reporting/comparisons${query}`);
  }

  async getReportingDashboard() {
    const cacheKey = buildApiCacheKey('GET', '/reporting/dashboard');
    return cachedApiGet(cacheKey, API_CACHE_TTL.reportingWorkspaceBundleMs, () =>
      this.request('/reporting/dashboard')
    );
  }

  async getReportingWorkspaceBundle(params: {
    include: string[];
    periods?: Partial<Record<string, string>>;
    includeAdvisory?: boolean;
    slim?: boolean;
  }) {
    const queryString = this.buildQueryString({
      include: params.include.join(','),
      footprintPeriod: params.periods?.footprint,
      comparisonsPeriod: params.periods?.comparisons,
      readinessPeriod: params.periods?.readiness,
      brsrPeriod: params.periods?.brsr,
      iso14064Period: params.periods?.iso14064,
      iso14067Period: params.periods?.iso14067,
      cbamPeriod: params.periods?.cbam,
      dashboardPeriod: params.periods?.dashboard,
      includeAdvisory: params.includeAdvisory ?? false,
      slim: params.slim ?? true,
    });
    const cacheKey = buildApiCacheKey(
      'GET',
      '/reporting/workspace-bundle',
      queryString.replace(/^\?/, '')
    );
    return cachedApiGet(cacheKey, API_CACHE_TTL.reportingWorkspaceBundleMs, () =>
      this.request(`/reporting/workspace-bundle${queryString}`)
    );
  }

  async getReportReadinessOverview(params?: { period?: string; includeAdvisory?: boolean }) {
    return this.request(`/reporting/readiness-overview${this.buildQueryString({
      period: params?.period,
      includeAdvisory: params?.includeAdvisory
    })}`);
  }

  async runReadinessAutopilot(params?: {
    period?: string;
    applyChanges?: boolean;
    runAgents?: boolean;
  }) {
    return this.request('/reporting/readiness-autopilot', {
      method: 'POST',
      body: JSON.stringify({
        period: params?.period || 'annual',
        applyChanges: params?.applyChanges !== false,
        runAgents: params?.runAgents !== false
      })
    });
  }

  async runComplianceHubAutopilot(params?: {
    period?: string;
    applyChanges?: boolean;
    runAgents?: boolean;
  }) {
    return this.request('/compliance-hub/readiness-autopilot', {
      method: 'POST',
      body: JSON.stringify({
        period: params?.period || 'annual',
        applyChanges: params?.applyChanges !== false,
        runAgents: params?.runAgents !== false
      })
    });
  }

  async confirmPackVerification(payload: {
    framework: string;
    field: string;
    confirmed?: boolean;
    note?: string;
  }) {
    return this.request('/compliance-hub/confirm-pack-verification', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async confirmDocumentVerification(payload: {
    documentId: string;
    confirmed?: boolean;
    note?: string;
  }) {
    return this.request('/compliance-hub/confirm-document-verification', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getReadinessAutopilotAuditLog(params?: { limit?: number; runId?: string }) {
    return this.request(`/compliance-hub/readiness-autopilot-audit-log${this.buildQueryString({
      limit: params?.limit,
      runId: params?.runId
    })}`);
  }

  async confirmCbamDocumentation(payload: {
    documentationId: string;
    confirmed?: boolean;
    note?: string;
    documentIds?: string[];
  }) {
    return this.request('/compliance-hub/confirm-cbam-documentation', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getBRSRReport(params?: { period?: string }) {
    return this.request(`/reporting/brsr${this.buildQueryString({
      period: params?.period,
      format: 'json'
    })}`);
  }

  async downloadBRSRReportPdf(params?: { period?: string }) {
    return this.requestBlob('/reporting/brsr', {
      period: params?.period,
      format: 'pdf'
    });
  }

  async downloadCbamReportPdf(params?: { period?: string }) {
    return this.requestBlob('/reporting/cbam', {
      period: params?.period,
      format: 'pdf'
    });
  }

  async downloadISO14064ReportPdf(params?: { period?: string; baseYear?: number }) {
    return this.requestBlob('/reporting/iso-14064', {
      period: params?.period,
      baseYear: params?.baseYear,
      format: 'pdf'
    });
  }

  async downloadISO14067ReportPdf(params?: { period?: string; functionalUnit?: string; allocationMethod?: string }) {
    return this.requestBlob('/reporting/iso-14067', {
      period: params?.period,
      functionalUnit: params?.functionalUnit,
      allocationMethod: params?.allocationMethod,
      format: 'pdf'
    });
  }

  async getConsultantReview(): Promise<ApiResponse> {
    return this.request('/msme/consultant-review');
  }

  async updateConsultantReviewScope(scope: 'required' | 'all'): Promise<ApiResponse> {
    return this.request('/msme/consultant-review', {
      method: 'PATCH',
      body: JSON.stringify({ scope }),
    });
  }

  async exportReportArtifact(params: {
    reportType: string;
    format: string;
    dateRange: string;
    reportId?: string;
  }): Promise<{ blob: Blob; fileName: string }> {
    const url = `${API_BASE_URL}/reporting/export`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = getClientAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, this.buildFetchInit({
      method: 'POST',
      headers,
      body: JSON.stringify({
        reportType: params.reportType,
        format: params.format,
        dateRange: params.dateRange,
        reportId: params.reportId || `EXP-${Date.now()}`,
      }),
    }, this.resolveRequestTimeoutMs('/reporting/export')));

    const contentType = response.headers.get('Content-Type') || '';
    const disposition = response.headers.get('Content-Disposition');

    if (!response.ok) {
      const errorBody = contentType.includes('application/json')
        ? await response.json()
        : await response.text();
      throw this.createHttpError(errorBody, response.status, '/reporting/export');
    }

    if (contentType.includes('application/json')) {
      const json = await response.json();
      const payload = json?.data ?? json;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const fallbackName = `${params.reportType}_${params.format}_${new Date().toISOString().slice(0, 10)}.json`;
      return {
        blob,
        fileName: this.parseContentDispositionFilename(disposition, fallbackName),
      };
    }

    const blob = await response.blob();
    const fallbackName = `${params.reportType}_${params.format}_${new Date().toISOString().slice(0, 10)}`;
    return {
      blob,
      fileName: this.parseContentDispositionFilename(disposition, fallbackName),
    };
  }

  async emailReport(params: { reportType: string; dateRange?: string; recipientEmail: string }) {
    return this.request('/reporting/email', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // GIFT Scheme endpoints
  async getGIFTSchemes(params?: { category?: string; status?: string; search?: string; page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    
    const queryString = queryParams.toString();
    const path = `/gift-schemes${queryString ? `?${queryString}` : ''}`;
    return cachedApiGet(
      buildApiCacheKey('GET', '/gift-schemes', queryString),
      API_CACHE_TTL.profileMs,
      () => this.request(path)
    );
  }

  async getGIFTScheme(id: string) {
    return this.request(`/gift-schemes/${id}`);
  }

  async checkGIFTSchemeEligibility(schemeId: string) {
    return this.request(`/gift-schemes/${schemeId}/eligibility`);
  }

  // ADEETIE (BEE energy-efficiency scheme)
  async getAdeetieScheme() {
    return this.request('/adeetie/scheme');
  }

  async getAdeetieClusters(params?: { sectorId?: string; state?: string }) {
    const query = this.buildQueryString(params || {}).replace(/^\?/, '');
    return cachedApiGet(
      buildApiCacheKey('GET', '/adeetie/clusters', query),
      API_CACHE_TTL.profileMs,
      () => this.request(`/adeetie/clusters${query ? `?${query}` : ''}`)
    );
  }

  async getAdeetieOverview() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/adeetie/overview'),
      API_CACHE_TTL.profileMs,
      () => this.request('/adeetie/overview')
    );
  }

  async getAdeetieEligibility(loanAmount?: number) {
    return this.request(
      `/adeetie/eligibility${this.buildQueryString(loanAmount != null ? { loanAmount } : {})}`
    );
  }

  async getAdeetieReadiness() {
    return this.request('/adeetie/readiness');
  }

  async calculateAdeetieSubvention(body: {
    loanAmount: number;
    nominalInterestRatePercent?: number;
    tenureYears?: number;
  }) {
    return this.request('/adeetie/subvention-calculator', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async getAdeetieDprBrief() {
    return this.request('/adeetie/dpr-brief');
  }

  async updateAdeetieProfile(body: {
    beeSector?: string;
    adeetieClusterId?: string;
    clusterAssociation?: string;
  }) {
    return this.request('/adeetie/profile', {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  async updateAdeetieJourney(body: { stage: string; notes?: string }) {
    return this.request('/adeetie/journey', {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  // GIFT Application endpoints
  async getGIFTApplications(params?: { status?: string; search?: string; page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    
    const queryString = queryParams.toString();
    return this.request(`/gift-applications${queryString ? `?${queryString}` : ""}`);
  }

  async getGIFTApplication(id: string) {
    return this.request(`/gift-applications/${id}`);
  }

  async createGIFTApplication(data: any) {
    return this.request("/gift-applications", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateGIFTApplication(id: string, data: any) {
    return this.request(`/gift-applications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async submitGIFTApplication(id: string) {
    return this.request(`/gift-applications/${id}/submit`, {
      method: "POST",
    });
  }

  // Carbon trading endpoints
  async getCarbonTradingPortfolio() {
    return cachedApiGet(buildApiCacheKey('GET', '/carbon/trading/portfolio'), 0, () =>
      this.request<ApiResponse<CarbonTradingPortfolio>>('/carbon/trading/portfolio')
    );
  }

  async getCarbonOffsetOptions(params?: {
    type?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
    verifiedBy?: string;
    page?: number;
    limit?: number;
  }) {
    const query = this.buildQueryString({
      type: params?.type,
      minPrice: params?.minPrice,
      maxPrice: params?.maxPrice,
      location: params?.location,
      verifiedBy: params?.verifiedBy,
      page: params?.page,
      limit: params?.limit,
    });
    return cachedApiGet(
      buildApiCacheKey('GET', '/carbon/trading/offsets', query.replace(/^\?/, '')),
      0,
      () => this.request<ApiResponse<CarbonOffsetOption[]>>(`/carbon/trading/offsets${query}`)
    );
  }

  async purchaseCarbonOffset(data: { offsetId: string; amount: number; pricePerTon: number }) {
    const result = await this.request('/carbon/trading/purchase', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateCarbonReadCache();
    return result;
  }

  async offsetCarbonCredits(data: { amount: number; description?: string }) {
    const result = await this.request('/carbon/trading/offset', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateCarbonReadCache();
    return result;
  }

  async getCarbonTradingHistory(params?: { page?: number; limit?: number; type?: string }) {
    return this.request<ApiResponse<{ transactions: CarbonTradingTransaction[] }>>(
      `/carbon/trading/history${this.buildQueryString({
        page: params?.page,
        limit: params?.limit,
        type: params?.type,
      })}`
    );
  }

  async getCarbonMarketTrends(params?: { period?: '7d' | '30d' | '90d' }) {
    return this.request<ApiResponse<{ priceTrends: { _id: string; averagePrice: number }[] }>>(
      `/carbon/trading/market-trends${this.buildQueryString({
        period: params?.period,
      })}`
    );
  }

  // Indian Carbon Market pool credits (earned / transferred / retired)
  async getMyCarbonPoolCredits() {
    return cachedApiGet(buildApiCacheKey('GET', '/carbon-credits/my-credits'), 0, () =>
      this.request<ApiResponse<MSMECarbonCreditsAccount>>('/carbon-credits/my-credits')
    );
  }

  async getIndianCarbonMarketIntegration() {
    return cachedApiGet(buildApiCacheKey('GET', '/carbon-credits/icm/integration'), 0, () =>
      this.request<ApiResponse<Record<string, unknown>>>('/carbon-credits/icm/integration')
    );
  }

  async getCarbonCreditsMarket() {
    return cachedApiGet(buildApiCacheKey('GET', '/carbon-credits/market'), 0, () =>
      this.request<ApiResponse<CarbonCreditsPoolMarket>>('/carbon-credits/market')
    );
  }

  async getCarbonCreditsPool() {
    return this.request<ApiResponse<Record<string, unknown>>>('/carbon-credits/pool');
  }

  async retirePoolCarbonCredits(data: { amount: number; reason?: string }) {
    const result = await this.request('/carbon-credits/retire', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateCarbonReadCache();
    return result;
  }

  async transferPoolCarbonCredits(data: {
    toMSMEId: string;
    amount: number;
    description?: string;
  }) {
    const result = await this.request('/carbon-credits/transfer', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateCarbonReadCache();
    return result;
  }

  async getCarbonCreditsTransactions(params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) {
    return this.request<
      ApiResponse<{
        transactions: CarbonCreditPoolTransaction[];
        pagination?: { current: number; pages: number; total: number };
      }>
    >(
      `/carbon-credits/transactions${this.buildQueryString({
        page: params?.page,
        limit: params?.limit,
        type: params?.type,
        status: params?.status,
      })}`
    );
  }

  async getCarbonCreditsLeaderboard(params?: { limit?: number; period?: string }) {
    return this.request<ApiResponse<{ leaderboard: unknown[]; period?: string }>>(
      `/carbon-credits/leaderboard${this.buildQueryString({
        limit: params?.limit,
        period: params?.period,
      })}`
    );
  }

  async getReports() {
    return this.request('/reporting');
  }

  async generateReport(type: string, params?: Record<string, unknown>) {
    return this.request('/reporting/generate', {
      method: 'POST',
      body: JSON.stringify({
        reportType: type,
        ...(params || {}),
      }),
    });
  }

  async orchestrateEmissionsReporting(params?: {
    period?: string;
    frameworks?: string[];
    msmeId?: string;
  }) {
    const periodMap: Record<string, string> = {
      '1month': 'month',
      '3months': 'quarter',
      '6months': '6months',
      '1year': 'annual',
    };
    return this.request('/orchestration-manager/emissions-reporting', {
      method: 'POST',
      body: JSON.stringify({
        period: periodMap[params?.period || ''] || params?.period || 'annual',
        frameworks: params?.frameworks,
        msmeId: params?.msmeId,
        triggerSource: 'reporting_ui',
      }),
    });
  }

  // Data Privacy endpoints
  async getPrivacySettings() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/data-privacy/settings'),
      API_CACHE_TTL.sessionMs,
      () => this.request('/data-privacy/settings')
    );
  }

  async updatePrivacySettings(data: any) {
    const result = await this.request('/data-privacy/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/data-privacy/settings'));
    return result;
  }

  async getDataRequests() {
    return this.request('/data-privacy/requests');
  }

  async submitDataRequest(data: any) {
    const result = await this.request('/data-privacy/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/data-privacy/dpdp/dsar-summary'));
    return result;
  }

  async getDataActivities() {
    return this.request('/data-privacy/activities');
  }

  async downloadPersonalData() {
    return this.request('/data-privacy/download');
  }

  async updateConsentPreferences(data: any) {
    return this.request('/data-privacy/consent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDpdpDsarSummary() {
    return cachedApiGet(
      buildApiCacheKey('GET', '/data-privacy/dpdp/dsar-summary'),
      API_CACHE_TTL.sessionMs,
      () => this.request('/data-privacy/dpdp/dsar-summary')
    );
  }

  async acknowledgeDpdpErasure(requestId: string, note?: string) {
    return this.request('/data-privacy/dpdp/acknowledge-erasure', {
      method: 'POST',
      body: JSON.stringify({ requestId, note }),
    });
  }

  async getInferredFieldConfirmations(params?: { period?: string }) {
    return this.request(`/compliance-hub/inferred-field-confirmations${this.buildQueryString({ period: params?.period })}`);
  }

  async confirmInferredField(data: { confirmKey: string; confirmed?: boolean; note?: string }) {
    const result = await this.request('/compliance-hub/confirm-inferred-field', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async getPublicPricingPlans() {
    return this.request('/billing/plans/public');
  }

  async getPublicBlogArticles(params?: {
    page?: number;
    limit?: number;
    featured?: boolean;
    category?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.featured) query.set('featured', 'true');
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/blog-articles${suffix}`);
  }

  async getPublicBlogArticleBySlug(slug: string) {
    return this.request(`/blog-articles/slug/${encodeURIComponent(slug)}`);
  }

  async getAdminBlogArticles(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/admin/blog-articles${suffix}`);
  }

  async getAdminBlogArticle(id: string) {
    return this.request(`/admin/blog-articles/${id}`);
  }

  async createAdminBlogArticle(data: Record<string, unknown>) {
    return this.request('/admin/blog-articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminBlogArticle(id: string, data: Record<string, unknown>) {
    return this.request(`/admin/blog-articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminBlogArticle(id: string) {
    return this.request(`/admin/blog-articles/${id}`, {
      method: 'DELETE',
    });
  }

  async seedAdminBlogArticles(force = false) {
    return this.request('/admin/blog-articles/seed-starter', {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  async publishAdminBlogArticle(id: string) {
    return this.request(`/admin/blog-articles/${id}/publish`, {
      method: 'POST',
    });
  }

  async publishDailyAdminBlogArticle() {
    return this.request('/admin/blog-articles/publish-daily', {
      method: 'POST',
    });
  }

  async getBillingStatus(options?: { fresh?: boolean }) {
    const key = buildApiCacheKey('GET', '/billing/status');
    if (options?.fresh) {
      clearApiRequestCache(key);
    }
    return cachedApiGet(key, API_CACHE_TTL.billingStatusMs, () =>
      this.request('/billing/status')
    );
  }

  async getBillingQuote() {
    return this.request('/billing/quote');
  }

  async createBillingCheckout(data: {
    pricingModel: 'usage_based' | 'fixed_plan';
    planId?: string;
  }) {
    const result = await this.request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/billing/status'));
    return result;
  }

  async verifyBillingPayment(data: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) {
    const result = await this.request('/billing/verify-payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    clearApiRequestCache(buildApiCacheKey('GET', '/billing/status'));
    return result;
  }

  /** Razorpay Standard Web Checkout — POST /api/create-order */
  async createRazorpayOrder(data: {
    amount: number;
    currency?: string;
    receipt?: string;
    pricingModel?: 'usage_based' | 'fixed_plan';
    planId?: string;
  }) {
    return this.request('/create-order', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /** Razorpay Standard Web Checkout — POST /api/verify-payment */
  async verifyRazorpayPayment(data: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) {
    return this.request('/verify-payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Compliance hub (India + export tracks)
  private async buildComplianceLegacySignals(period = 'annual') {
    const enterpriseProfile = await this.getEnterpriseProfile().catch(() => null);
    const useEnterprise = Boolean(enterpriseProfile?.data?.companyName);

    const [profileResponse, statsResponse, brsrResponse] = await Promise.all([
      useEnterprise
        ? Promise.resolve(enterpriseProfile)
        : this.getMSMEProfile().catch(() => null),
      useEnterprise ? Promise.resolve(null) : this.getMSMEStats().catch(() => null),
      this.getBRSRReport({ period }).catch(() => null),
    ]);

    const profile = this.unwrapApiData<Record<string, unknown>>(profileResponse);
    const stats = this.unwrapApiData<Record<string, unknown>>(statsResponse);
    const brsr = this.unwrapApiData<Record<string, unknown>>(brsrResponse);
    const carbon = (stats?.carbon ?? {}) as Record<string, unknown>;
    const brsrCompliance = (brsr?.compliance ?? {}) as Record<string, unknown>;
    const brsrEmissions = (brsr?.emissions ?? {}) as Record<string, unknown>;
    const totalEmissionsKg = Number(
      carbon.totalCO2Emissions ?? brsrEmissions.totalGHG ?? 0
    );
    const hasEmissions = Number.isFinite(totalEmissionsKg) && totalEmissionsKg > 0;
    const brsrReady = Boolean(
      brsrCompliance.isBRSRCompliant || brsrCompliance.disclosurePrepReady
    );

    return {
      period,
      companyName: String(profile?.companyName ?? 'Your company'),
      totalEmissionsKg,
      hasEmissions,
      brsrReady,
      brsr,
    };
  }

  private buildComplianceIndiaReadiness(signals: {
    hasEmissions: boolean;
    brsrReady: boolean;
  }) {
    return {
      brsrCore: signals.brsrReady
        ? 'aligned'
        : signals.hasEmissions
          ? 'needs_improvement'
          : 'needs_activity_data',
      isoAssurance: 'in_progress',
      patIntensity: signals.hasEmissions ? 'data_available' : 'needs_activity_data',
      sbti: 'not_started',
      zedCertification: 'not_started',
      accountingSync: 'import_only',
    };
  }

  private buildComplianceExportReadiness(signals: { hasEmissions: boolean }) {
    return {
      cbam: 'review_required',
      productPcf: 'needs_products',
      csrd: signals.hasEmissions ? 'datapoints_available' : 'needs_inventory',
      cdp: signals.hasEmissions ? 'datapoints_available' : 'needs_inventory',
      tcfd: 'template_ready',
      supplierPortal: 'empty',
      eudr: 'not_applicable',
      ecovadis: 'template_ready',
    };
  }

  private async buildComplianceFallback(
    track: 'overview' | 'india' | 'export',
    params?: { period?: string }
  ): Promise<ApiResponse> {
    const signals = await this.buildComplianceLegacySignals(params?.period ?? 'annual');
    const indiaReadiness = this.buildComplianceIndiaReadiness(signals);
    const exportReadiness = this.buildComplianceExportReadiness(signals);
    const generatedAt = new Date().toISOString();

    if (track === 'overview') {
      return {
        success: true,
        data: {
          period: signals.period,
          companyName: signals.companyName,
          totalEmissionsKgCo2e: signals.totalEmissionsKg,
          indiaReadiness,
          exportReadiness,
          generatedAt,
        },
      };
    }

    if (track === 'india') {
      return {
        success: true,
        data: {
          track: 'india',
          period: signals.period,
          generatedAt,
          readiness: indiaReadiness,
          modules: {
            brsrCoreSupplierPack: signals.brsr ?? {},
            iso14064Assurance: { overallReadinessScore: 0, checkpoints: [] },
            patEnergyIntensity: { energySharePercent: signals.hasEmissions ? 1 : 0 },
            sbtiTargets: { status: 'not_started' },
            actionPlans: [],
            productFootprints: [],
            zedCertification: {},
            accountingSync: [],
          },
        },
      };
    }

    return {
      success: true,
      data: {
        track: 'export',
        period: signals.period,
        generatedAt,
        exportProfile: {},
        readiness: exportReadiness,
        modules: {
          cbamExporter: {},
          productFootprints: [],
          csrdSupplier: {},
          cdpClimate: {},
          tcfdIssb: {},
          supplierQuestionnaires: [],
          eudr: { applicable: false },
          ecovadis: {},
        },
        packValidation: {},
      },
    };
  }

  private async requestComplianceReadEndpoint(
    track: 'overview' | 'india' | 'export',
    params?: { period?: string }
  ): Promise<ApiResponse> {
    const query = this.buildQueryString({ period: params?.period });
    const pathByTrack = {
      overview: ['/compliance-hub/overview', '/compliance/overview'],
      india: ['/compliance-hub/india', '/compliance/india'],
      export: ['/compliance-hub/export', '/compliance/export'],
    };
    const primaryPath = pathByTrack[track][0];
    const cacheKey = buildApiCacheKey('GET', primaryPath, query.replace(/^\?/, ''));

    return cachedApiGet(cacheKey, API_CACHE_TTL.complianceHubMs, async () => {
      try {
        return await this.requestFirstAvailable(
          pathByTrack[track].map((path) => `${path}${query}`)
        );
      } catch (err) {
        if (!this.isRouteNotFoundError(err)) {
          throw err;
        }
        return this.buildComplianceFallback(track, params);
      }
    });
  }

  async getComplianceHubOverview(params?: { period?: string }) {
    return this.requestComplianceReadEndpoint('overview', params);
  }

  async getComplianceIndiaTrack(params?: { period?: string }) {
    return this.requestComplianceReadEndpoint('india', params);
  }

  async getComplianceExportTrack(params?: { period?: string }) {
    return this.requestComplianceReadEndpoint('export', params);
  }

  async getComplianceHubRecord() {
    return this.request('/compliance-hub/record');
  }

  async updateComplianceSbtiTargets(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/sbti-targets', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async updateComplianceAssurance(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/assurance', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async updateComplianceZedCertification(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/zed-certification', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async updateComplianceExportProfile(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/export-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async updateComplianceBrsrFilingProfile(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/brsr-filing-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async createComplianceActionPlan(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/action-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async updateComplianceActionPlan(planId: string, data: Record<string, unknown>) {
    const result = await this.request(`/compliance-hub/action-plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async createSupplierQuestionnaire(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/supplier-questionnaires', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateComplianceReadCache();
    return result;
  }

  async listBuyerQuestionTemplates() {
    return this.request('/compliance-hub/buyer-question-templates');
  }

  async importBuyerQuestionnaireTemplate(data: {
    templateId: string;
    buyerName: string;
    buyerContactEmail?: string;
    dueDate?: string;
  }) {
    return this.request('/compliance-hub/supplier-questionnaires/import-template', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAssuranceClauseWorkflows(params?: { period?: string; framework?: string }) {
    return this.request(
      `/compliance-hub/assurance-clause-workflows${this.buildQueryString({
        period: params?.period,
        framework: params?.framework,
      })}`
    );
  }

  async updateSupplierQuestionnaire(questionnaireId: string, data: Record<string, unknown>) {
    return this.request(`/compliance-hub/supplier-questionnaires/${questionnaireId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async prefillSupplierQuestionnaireFromInventory(
    questionnaireId: string,
    data?: { period?: string; overwrite?: boolean }
  ) {
    return this.request(`/compliance-hub/supplier-questionnaires/${questionnaireId}/prefill-from-inventory`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async prefillAllSupplierQuestionnairesFromInventory(data?: { period?: string; overwrite?: boolean }) {
    return this.request('/compliance-hub/supplier-questionnaires/prefill-all-from-inventory', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async validateSupplierQuestionnairePortal(
    questionnaireId: string,
    data?: { period?: string }
  ) {
    return this.request(`/compliance-hub/supplier-questionnaires/${questionnaireId}/validate-portal`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async validateAllSupplierQuestionnairesPortal(data?: { period?: string }) {
    return this.request('/compliance-hub/supplier-questionnaires/validate-all-portal', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async syncBrsrSgFromHrErp(data?: { period?: string; overwrite?: boolean }) {
    return this.request('/compliance-hub/brsr-sg/sync-hr-erp', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async validateBrsrXbrlExchange(data?: { period?: string; exchange?: string }) {
    return this.request('/compliance-hub/brsr-xbrl/validate-exchange', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async updateEudrPlots(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/eudr/plots', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async updateGroupBoundary(data: Record<string, unknown>) {
    const result = await this.request('/compliance-hub/group-boundary', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateMsmeReadCache();
    return result;
  }

  async getExportGapClosure(params?: { period?: string }) {
    return this.request(`/compliance-hub/export-gap-closure${this.buildQueryString({ period: params?.period })}`);
  }

  async getUpstreamSuppliers() {
    return this.request('/compliance-hub/upstream-suppliers');
  }

  async createUpstreamSupplier(data: Record<string, unknown>) {
    return this.request('/compliance-hub/upstream-suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async inviteUpstreamSupplier(data: {
    vendorName?: string;
    legalName?: string;
    contactEmail: string;
    contactName?: string;
    inviteMessage?: string;
    supplierId?: string;
  }) {
    return this.request('/compliance-hub/upstream-suppliers/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSupplierEmissionSubmissions(params?: { supplierId?: string }) {
    return this.request(
      `/compliance-hub/upstream-suppliers/emission-submissions${this.buildQueryString({
        supplierId: params?.supplierId,
      })}`
    );
  }

  async createSupplierEmissionSubmission(data: Record<string, unknown>) {
    return this.request('/compliance-hub/upstream-suppliers/emission-submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async importSupplierEmissionSubmissions(data: Record<string, unknown>) {
    return this.request('/compliance-hub/upstream-suppliers/emission-submissions/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async syncSupplierTier1Data(data?: Record<string, unknown>) {
    return this.request('/compliance-hub/upstream-suppliers/sync-tier1', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async createProductFootprint(data: Record<string, unknown>) {
    return this.request('/compliance-hub/product-footprints', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProductFootprint(footprintId: string, data: Record<string, unknown>) {
    return this.request(`/compliance-hub/product-footprints/${footprintId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getComplianceExportPack(framework: string, params?: { period?: string }) {
    return this.request(
      `/compliance-hub/export-pack/${framework}${this.buildQueryString({ period: params?.period })}`
    );
  }

  async downloadBuyerPackExport(params?: { period?: string; includeEudr?: boolean; includeCbam?: boolean }) {
    return this.requestBlob('/compliance-hub/buyer-pack-export', {
      period: params?.period,
      includeEudr: params?.includeEudr,
      includeCbam: params?.includeCbam,
    });
  }

  async getBrsrCoreSupplierPack(params?: { period?: string }) {
    return this.request(`/compliance-hub/india-pack/brsr-core${this.buildQueryString({ period: params?.period })}`);
  }

  async getComplianceAccountingConnectors(params?: { period?: string }) {
    return this.request(`/compliance-hub/accounting-connectors${this.buildQueryString({ period: params?.period })}`);
  }

  async getComplianceIcmRegistrySnapshot(params?: { period?: string }) {
    return this.request(`/compliance-hub/icm-registry-snapshot${this.buildQueryString({ period: params?.period })}`);
  }

  async getComplianceVendorP2Rollout(params?: { period?: string }) {
    return this.request(`/compliance-hub/vendor-p2-rollout${this.buildQueryString({ period: params?.period })}`);
  }

  async saveMeasuredGasInventory(entries: Array<Record<string, unknown>>) {
    return this.request('/compliance-hub/gas-by-gas/measured', {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    });
  }

  async importEcovadisAssessment(data: { score: number; assessedAt?: string; source?: string; note?: string }) {
    return this.request('/compliance-hub/ecovadis/assessment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAdminDsarQueue(params?: { status?: string; limit?: number }) {
    return this.request(`/data-privacy/admin/dsar-queue${this.buildQueryString({
      status: params?.status,
      limit: params?.limit,
    })}`);
  }

  async updateAdminDsarRequest(id: string, data: { status?: string; processingNotes?: string; assignedTo?: string }) {
    return this.request(`/data-privacy/admin/dsar-queue/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Enterprise / large corporation endpoints
  async getEnterpriseProfile() {
    return this.request('/enterprise/profile');
  }

  async registerEnterprise(data: Record<string, unknown>) {
    return this.request('/enterprise/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEnterpriseProfile(data: Record<string, unknown>) {
    return this.request('/enterprise/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateEnterpriseOperations(data: Record<string, unknown>) {
    return this.request('/enterprise/operations', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getEnterpriseScope3Template() {
    return this.request('/enterprise/scope3-template');
  }

  async runEnterpriseInventoryAssessment(params?: {
    startDate?: string;
    endDate?: string;
    runAgents?: boolean;
  }) {
    const result = await this.request('/enterprise/inventory/assess', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    invalidateCarbonReadCache();
    return result;
  }

  async getEnterpriseWorkflow() {
    return this.request('/enterprise/workflow');
  }

  async getEnterpriseSectionGuidance(sectionKey: string) {
    return this.request(`/enterprise/workflow/section/${sectionKey}/guidance`, {
      method: 'POST',
    });
  }

  async completeEnterpriseWorkflowSection(sectionKey: string) {
    return this.request('/enterprise/workflow/complete-section', {
      method: 'POST',
      body: JSON.stringify({ sectionKey }),
    });
  }

  async orchestrateEnterpriseEmissions() {
    return this.request('/enterprise/orchestrate', { method: 'POST' });
  }

  async getEnterpriseIntelligence(params?: {
    period?: string;
    runAgents?: boolean;
    includeForecast?: boolean;
  }) {
    return this.request(
      `/enterprise/intelligence${this.buildQueryString({
        period: params?.period,
        runAgents: params?.runAgents,
        includeForecast: params?.includeForecast,
      })}`
    );
  }

  async syncEnterpriseConnectors() {
    return this.request('/enterprise/sync-connectors', { method: 'POST' });
  }

  async getEnterprisePricing() {
    return this.request('/enterprise/pricing');
  }

  async getIndustryPeerBenchmark() {
    return this.request('/platform/industry-peer-benchmark');
  }

  async getSupplierCarbonScores(limit = 25) {
    return this.request(`/platform/supplier-carbon-scores${this.buildQueryString({ limit })}`);
  }

  async buildWhatsAppSharePayload(data: {
    reportType?: string;
    period?: string;
    readinessLevel?: string;
    totalKgCo2e?: number;
  }) {
    return this.request('/platform/whatsapp-share', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getScheduledReports() {
    return this.request('/platform/scheduled-reports');
  }

  async createScheduledReport(data: {
    reportType?: string;
    format?: string;
    frequency?: string;
    recipients?: string[];
    subject?: string;
  }) {
    return this.request('/platform/scheduled-reports', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async updateScheduledReport(scheduleId: string, data: {
    frequency?: string;
    format?: string;
    recipients?: string[];
    subject?: string;
    enabled?: boolean;
  }) {
    return this.request(`/platform/scheduled-reports/${scheduleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data || {}),
    });
  }

  async deleteScheduledReport(scheduleId: string) {
    return this.request(`/platform/scheduled-reports/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  async getDraftReadinessSuggestions() {
    return this.request('/platform/draft-suggestions');
  }

  async confirmDraftReadinessSuggestions(scopes?: string[]) {
    return this.request('/platform/draft-suggestions/confirm', {
      method: 'POST',
      body: JSON.stringify({ scopes: scopes || [] }),
    });
  }

  async rejectDraftReadinessSuggestions(scopes?: string[], reason?: string) {
    return this.request('/platform/draft-suggestions/reject', {
      method: 'POST',
      body: JSON.stringify({ scopes: scopes || [], reason: reason || null }),
    });
  }

  async downloadCarbonFootprintCsv() {
    return this.requestBlob('/platform/carbon-footprint/export.csv');
  }
}

const apiService = new ApiService();
export default apiService;
export { clearApiRequestCache };